import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Package, ArrowLeft, Printer, CheckCircle2,
  ShoppingCart, Check, Star, Bell, AlertTriangle, Layers,
  FileText, Tag, Barcode, Hash, Warehouse, Settings, Zap, Timer, Recycle, Search,
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { BoxNudge } from '../components/product/BoxNudge';
import { PRODUCT_BOX_DISCOUNTS } from '../data/boxDiscount';
import { toast } from 'sonner';
import { getProductImages } from '../data/productImages';
import { ProductGallery } from '../components/product/ProductGallery';
import { Product } from '../types';
import { useFavorites } from '../context/PortalContext';
import { useRecentlyViewed } from '../context/ProductContext';
import { QtyInput } from '../components/product/QtyInput';
import { StockWarning } from '../components/product/StockWarning';
import { LoginToBuy } from '../components/product/LoginToBuy';
import { KitDetailModal } from '../components/product/KitDetailModal';
import { StockArrivalBadge } from '../components/product/StockArrivalBadge';
import { RibassoBadge, PromoLineBadge } from '../components/product/RibassoBadge';
import { RecentlyViewed } from '../components/product/RecentlyViewed';
import { ColorDot } from '../components/product/ColorDot';
import { ProductAttachments } from '../components/product/ProductAttachments';
import { productService } from '../services/productService';
import { useMyStockAlerts } from '../hooks/useMyStockAlerts';
import { difformityService } from '../services/difformityService';

// ── Skeleton loading ───────────────────────────────────────────────────────
function SkeletonLine({ w = 'full', h = 4 }: { w?: string; h?: number }) {
  return (
    <div
      className={`bg-gray-200 animate-pulse rounded w-${w} h-${h}`}
      style={{ height: `${h * 4}px` }}
    />
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────
function formatPrice(val: number | null | undefined): string | null {
  if (val == null || val <= 0) return null;
  return val.toFixed(2).replace('.', ',');
}

function usePromoCountdown(endIso?: string | null): { text: string; expired: boolean } | null {
  const target = endIso ? new Date(endIso).getTime() : 0;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!endIso) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [endIso]);
  if (!endIso) return null;
  const diff = Math.max(0, target - now);
  if (diff === 0) return { text: 'Scaduta', expired: true };
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff / 3600000) % 24);
  const minutes = Math.floor((diff / 60000) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  const pad = (n: number) => String(n).padStart(2, '0');
  const text = `${days > 0 ? `${days}g ` : ''}${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
  return { text, expired: false };
}

export default function ProductDetailPage(): React.ReactElement {
  const { categoryId, sku } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fromModelId = searchParams.get('from_model');

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [compatModels, setCompatModels] = useState<Array<{ id: string; nome: string; marca: string }>>([]);
  const [showKitModal, setShowKitModal] = useState(false);
  const [addQty, setAddQty] = useState(1);
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportText, setReportText] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);
  const [compatQuery, setCompatQuery] = useState('');
  const [showAllCompat, setShowAllCompat] = useState(false);

  /* Un prodotto generico ha centinaia di modelli compatibili: senza memo
     il filtro girava su tutta la lista a ogni render della pagina, non
     solo alla digitazione. Deve stare qui sopra, prima dei return
     anticipati di loading/404. */
  const filteredCompatList = useMemo(() => {
    const q = compatQuery.trim().toLowerCase();
    if (!q) return compatModels;
    return compatModels.filter(m => `${m.marca} ${m.nome}`.toLowerCase().includes(q));
  }, [compatModels, compatQuery]);

  /* La barra acquisto sticky su mobile compare quando il buy box esce dal
     viewport: su un prodotto con centinaia di modelli compatibili la
     pagina è lunga 15+ schermate e la CTA sarebbe altrimenti
     irraggiungibile senza risalire. */
  const buyBoxRef = useRef<HTMLDivElement | null>(null);
  const [buyBoxOffscreen, setBuyBoxOffscreen] = useState(false);
  const { addAlert, removeAlert, isActive } = useMyStockAlerts();
  const { addItem, items: cartItems } = useCart();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { addViewed } = useRecentlyViewed();
  const { user, isLoading: authLoading } = useAuth();
  const isAdmin = user?.ruolo === 'admin';

  // Carica prodotto dal backend — attende il restore della sessione (bug
  // 26/02/2026, stesso fix di HomePage.tsx/OutletPage.tsx) per evitare che
  // la prima chiamata parta senza header Authorization su hard-refresh.
  useEffect(() => {
    if (!sku || authLoading) return;
    setLoading(true);
    let cancelled = false;
    productService.get(sku, { publicOnly: true })
      .then(p => { if (!cancelled) { setProduct(p); setLoading(false); } })
      .catch(() => { if (!cancelled) { setProduct(null); setLoading(false); } });
    return () => { cancelled = true; };
  }, [sku, authLoading]);

  // Modelli macchina compatibili (fonte: abbinatore_associazioni)
  useEffect(() => {
    if (!sku) { setCompatModels([]); return; }
    let cancelled = false;
    productService.compatibleModels(sku)
      .then(modelli => { if (!cancelled) setCompatModels(modelli); })
      .catch(() => { if (!cancelled) setCompatModels([]); });
    return () => { cancelled = true; };
  }, [sku]);

  // Track recently viewed
  useEffect(() => {
    if (product) addViewed(product);
  }, [product?.sku]); // eslint-disable-line

  // Osserva il buy box per pilotare la barra sticky. Dipende da `product`
  // perché il nodo osservato esiste solo dopo il caricamento (prima la
  // pagina rende lo skeleton e il ref è null).
  //
  // Non basta `!isIntersecting`: da quando il buy box sta sotto galleria e
  // titolo, al caricamento è già fuori dal viewport e la barra comparirebbe
  // subito, rimettendo prezzo e CTA prima del nome del prodotto — cioè
  // l'ordine che il riordino elimina. La barra subentra solo quando il buy
  // box è stato visto ed è uscito dall'alto.
  useEffect(() => {
    const node = buyBoxRef.current;
    if (!node) return;
    let seen = false;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          seen = true;
          setBuyBoxOffscreen(false);
          return;
        }
        const viewportTop = entry.rootBounds?.top ?? 0;
        const goneUp = entry.boundingClientRect.bottom <= viewportTop;
        setBuyBoxOffscreen(seen && goneUp);
      },
      { threshold: 0 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [product?.sku]); // eslint-disable-line

  const isInCart = cartItems.some(i => i.sku === product?.sku);
  const isFav = product ? isFavorite(product.sku) : false;

  // Stock
  const stockVal = product?.stock ?? 0;
  const hasStock = stockVal != null;
  const inStock = hasStock && stockVal > 0;
  const stockQty = hasStock ? Math.floor(stockVal) : 0;
  // Stock "residuo" = grezzo - già nel carrello (non può essere negativo)
  const alreadyInCart = cartItems.find(i => i.sku === product?.sku)?.qty || 0;
  const effectiveStock = Math.max(0, stockQty - alreadyInCart);
  const maxQty = effectiveStock > 0 ? Math.min(effectiveStock, 999) : 0;
  const isOutOfStock = hasStock && effectiveStock === 0;

  const { isAuthenticated } = useAuth();

  // Inizializza addQty a 1 quando il prodotto cambia (rimossa la logica
  // "minimo vendibile" — non più prevista, richiesta utente 15/02/2026).
  useEffect(() => {
    if (product) setAddQty(1);
  }, [product?.sku]); // eslint-disable-line

  // Web Vendibile (flag NTS ar_webvend): se esplicitamente false, il
  // prodotto è visibile in catalogo ma non acquistabile online.
  const isNotSellable = product?.webSellable === false;

  // Prezzo (con eventuale override promo)
  const promo = product?.promo;
  const promoActive = !!(promo?.priceOverride && promo.priceOverride > 0);
  const isPermanentPromo = promoActive && promo?.isPermanent === true;
  const basePrice = product?.listino10;
  const price = promoActive ? promo!.priceOverride : basePrice;
  const formattedPrice = formatPrice(price);
  const formattedListino = formatPrice(basePrice);
  const sconto = promoActive && basePrice && basePrice > promo!.priceOverride
    ? Math.round((1 - promo!.priceOverride / basePrice) * 100)
    : 0;
  // Il countdown è visibile solo per offerte a tempo (non permanenti)
  const promoCountdown = usePromoCountdown(
    promoActive && !isPermanentPromo ? promo?.endDate : null
  );


  // OEM
  const oemCodes = product?.oemCodes || [];

  // Titolo/descrizione: già unificati dal backend (_doc_to_product):
  // ``title``/``descrizione`` sono sempre descrWeb/noteWeb quando presenti,
  // stesso valore mostrato in pannello admin, area clienti e catalogo.
  const displayTitle = product?.title ?? '';
  const descrizione = product?.descrizione ?? '';

  // Kit
  const isKit = !!(product?.isKit && product?.kitItems && product.kitItems.length > 0);

  // Non deve mai essere possibile aggiungere al carrello più pezzi di
  // quanti siano effettivamente disponibili: quando la quantità richiesta
  // supera la giacenza, il pannello StockWarning (sopra il bottone) offre
  // le uniche 2 opzioni valide (ordina il disponibile + alert per il resto,
  // oppure solo il disponibile) — stesso guard già usato in
  // ProductSearchPage.handleAddToCart per la lista prodotti.
  const qtyExceedsStock = hasStock && effectiveStock > 0 && addQty > effectiveStock;

  const handleAddToCart = () => {
    if (!product || qtyExceedsStock) return;
    // Se la disponibilità basta solo in parte, l'avviso lo dà ``addItem``.
    if (addItem(product, addQty) === addQty) toast.success(`${product.sku} ×${addQty} aggiunto al carrello`);
  };

  // ── Loading skeleton ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="product-detail-loading">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="h-64 bg-gray-200 animate-pulse rounded-xl" />
            <div className="rounded-xl border bg-white p-6 space-y-3">
              <SkeletonLine w="48" h={6} />
              <SkeletonLine w="full" h={4} />
              <SkeletonLine w="3/4" h={4} />
            </div>
          </div>
          <div className="space-y-4">
            <div className="h-48 bg-gray-200 animate-pulse rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  // ── Not found ────────────────────────────────────────────────────────────
  if (!product) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 text-center">
        <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h1 className="text-2xl font-semibold">Prodotto non trovato</h1>
        <p className="text-sm text-muted-foreground mt-2 mb-6">
          Lo SKU <strong>{sku}</strong> non è presente nel catalogo.
        </p>
        <Link
          to={`/prodotti/${categoryId || 'toner'}`}
          className="text-sm text-[hsl(var(--dtk-focus))] hover:underline inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Torna al catalogo
        </Link>
      </div>
    );
  }

  const compatList = compatModels;
  /* Con il filtro attivo si mostra tutto ciò che corrisponde: chi filtra
     si aspetta di vedere i risultati, non i primi 8 dei risultati. */
  const COMPAT_PREVIEW = 8;
  const compatFiltering = compatQuery.trim().length > 0;
  const compatCollapsed = !compatFiltering && !showAllCompat;
  const visibleCompatList = compatCollapsed
    ? filteredCompatList.slice(0, COMPAT_PREVIEW)
    : filteredCompatList;
  const compatHiddenCount = filteredCompatList.length - visibleCompatList.length;

  return (
    <div data-testid="product-detail-page">
      {/* Context bar: arrived from model search */}
      {fromModelId && (
        <div className="bg-[hsl(214,58%,41%)] text-white" data-testid="from-model-context-bar">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex items-center gap-3 text-[13px]">
            <Printer className="w-3.5 h-3.5 text-white/70 flex-shrink-0" />
            <span className="text-white/70">Stai visualizzando un consumabile per un modello specifico</span>
            <Link
              to={`/consumabili-per/${encodeURIComponent(fromModelId)}`}
              className="ml-auto text-white/80 hover:text-white text-[12px] flex items-center gap-1 hover:underline"
              data-testid="back-to-model-results"
            >
              <ArrowLeft className="w-3 h-3" /> Torna ai risultati
            </Link>
          </div>
        </div>
      )}

      {/* Bottone backoffice per admin — breadcrumb rimosso (richiesta utente 26/02/2026) */}
      {isAdmin && (
        <div className="border-b bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-end">
            <Link
              to={`/admin/prodotti/${product.sku}`}
              data-testid="open-in-backoffice-button"
              title="Apri questo prodotto nel pannello backoffice"
              className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-[11px] font-semibold bg-[hsl(214,58%,41%)] text-white hover:bg-[hsl(214,58%,46%)] transition-colors shadow-sm"
            >
              <Settings className="w-3 h-3" />
              Apri in backoffice
            </Link>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Back button — torna alla pagina precedente (cronologia browser) */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="product-detail-back-button"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Indietro
        </button>

        {/* ── Sequenza mobile ────────────────────────────────────────────
            Su mobile entrambe le colonne sono `contents`, quindi i blocchi
            sono figli diretti della griglia e `order-[N]` li dispone uno a
            uno. Da `lg` in su ogni wrapper torna un box e `lg:order-none`
            azzera la sequenza: il desktop resta a due colonne invariato.

              10  galleria
              20  titolo + Codice DTK
              30  buy box            (prezzo, quantità, CTA)
              40  attributi + descrizione + compatibilità + difformità
              50  giacenza magazzino
              60  informazioni       (garanzia, spedizione)
              70  assistenza
              80  visti di recente

            Giacenza e Informazioni chiudono con l'assistenza invece di
            stare sotto il buy box: la disponibilità al momento della
            decisione la dà già la riga "Disponibile (N pz)" dentro il buy
            box, e la coda della pagina è il posto delle card di servizio.

            Numerati a decine per poter inserire un blocco senza rinumerare.
            Prima il buy box era in testa (`order-1`): prezzo e CTA
            arrivavano prima ancora del nome del prodotto. */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ── Main column ─────────────────────────────────────────────── */}
          <div className="contents lg:block lg:col-span-2">
            {/* Product Gallery */}
            <div className="order-[10] lg:order-none" data-testid="product-gallery">
              <ProductGallery
                images={
                  product.images && product.images.length > 0
                    ? [...product.images].sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0)).map(i => i.url)
                    : getProductImages(product.sku, product.category)
                }
                sku={product.sku}
              />
            </div>

            {/* `contents` su mobile: la card non genera un box, così le sue
                due metà (intestazione e corpo) si ordinano separatamente
                attorno al buy box. Da `lg` in su torna la card unica di
                sempre — bordo, sfondo e padding sono qui, non sui figli. */}
            <div
              data-testid="product-detail-card"
              className="contents lg:block rounded-xl border bg-white p-6 sm:p-8 mt-4"
            >
              {/* Header */}
              <div className="order-[20] lg:order-none lg:mb-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <h1
                      className="text-xl sm:text-2xl font-semibold tracking-tight break-words"
                      data-testid="product-detail-title"
                    >
                      {displayTitle}
                    </h1>
                    <div className="mt-2 flex items-center gap-3 flex-wrap">
                      <span className="flex items-baseline gap-1.5">
                        <span className="text-xs font-medium text-muted-foreground">Codice DTK</span>
                        <span
                          className="text-lg font-mono font-bold text-[hsl(214,58%,41%)]"
                          data-testid="product-detail-sku"
                        >
                          {product.sku}
                        </span>
                      </span>
                      {product.seedOnly && (
                        <span
                          className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-200"
                          data-testid={`demo-badge-${product.sku}`}
                          title="Prodotto demo — non ancora presente nel gestionale NTS"
                        >
                          DEMO
                        </span>
                      )}
                      <PromoLineBadge lineaPromo={product.lineaPromo} />
                      {isKit && (
                        <button
                          type="button"
                          onClick={() => setShowKitModal(true)}
                          title="Visualizza scheda kit"
                          data-testid="product-detail-kit-badge"
                          className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100 hover:border-amber-300 transition-colors"
                        >
                          <Package className="w-3 h-3" />
                          Kit ({product.kitItems!.length} articoli)
                        </button>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      toggleFavorite(product);
                      toast.success(isFav ? 'Rimosso dai preferiti' : 'Aggiunto ai preferiti');
                    }}
                    className={`w-10 h-10 rounded-lg border flex items-center justify-center transition-colors flex-shrink-0 ${
                      isFav
                        ? 'bg-amber-100 border-amber-300 text-amber-600'
                        : 'hover:bg-secondary/60 text-muted-foreground'
                    }`}
                    data-testid="toggle-favorite"
                    aria-label={isFav ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
                  >
                    <Star className={`w-5 h-5 ${isFav ? 'fill-amber-500' : ''}`} />
                  </button>
                </div>
              </div>

              {/* Corpo della card. Su mobile è un blocco a sé che arriva
                  dopo il buy box, quindi si porta bordo, sfondo e padding;
                  su desktop li azzera e torna la seconda metà della card. */}
              <div
                data-testid="product-detail-card-body"
                className="order-[40] lg:order-none rounded-xl border bg-white p-6 lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0"
              >
                {/* Specs Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                  {/* Colore — sempre visibile, con pallino descrittivo */}
                  <div className="rounded-lg border p-3 bg-[hsl(var(--dtk-canvas))]">
                    <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Colore</div>
                    <div className="text-sm font-semibold mt-0.5 flex items-center gap-2" data-testid="product-color">
                      {product.color && product.color !== '-' ? product.color : '—'}
                      <ColorDot color={product.color} />
                    </div>
                  </div>
                  {/* Compatibile / Originale */}
                  <div className="rounded-lg border p-3 bg-[hsl(var(--dtk-canvas))]">
                    <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                      {product.type === 'Originale' ? <CheckCircle2 className="w-3 h-3" /> : <Recycle className="w-3 h-3" />}
                      Tipologia
                    </div>
                    <div className="text-sm font-semibold mt-0.5" data-testid="product-originale-compatibile">
                      {product.type || '—'}
                    </div>
                  </div>
                  {/* Barcode */}
                  {product.codiceBarcode && (
                    <div className="rounded-lg border p-3 bg-[hsl(var(--dtk-canvas))]">
                      <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                        <Barcode className="w-3 h-3" /> EAN/Barcode
                      </div>
                      <div className="text-sm font-mono mt-0.5" data-testid="product-barcode">
                        {product.codiceBarcode}
                      </div>
                    </div>
                  )}
                  {/* Resa */}
                  {product.yield && product.yield !== '-' && (
                    <div className="rounded-lg border p-3 bg-[hsl(var(--dtk-canvas))]">
                      <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Resa</div>
                      <div className="text-sm font-semibold mt-0.5">{product.yield}</div>
                    </div>
                  )}
                </div>

                {/* OEM multipli */}
                {oemCodes.length > 1 && (
                  <div className="mb-6 rounded-lg border bg-[hsl(var(--dtk-canvas))] p-4" data-testid="product-oem-codes">
                    <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <Tag className="w-3.5 h-3.5" /> OEM Compatibili
                    </h2>
                    <div className="flex flex-wrap gap-1.5">
                      {oemCodes.map(oem => (
                        <span
                          key={oem}
                          className="text-xs font-mono px-2 py-0.5 rounded border bg-white text-[hsl(214,58%,41%)] border-[hsl(210,40%,80%)]"
                        >
                          {oem}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Composizione del Kit */}
                {isKit && (
                  <div
                    className="mb-6 rounded-xl border-2 border-amber-200 bg-gradient-to-br from-amber-50/60 to-white overflow-hidden"
                    data-testid="product-detail-kit-composition"
                  >
                    <div className="px-4 py-3 border-b border-amber-200 bg-amber-50/80 flex items-center gap-2">
                      <div className="w-7 h-7 rounded-md bg-amber-100 flex items-center justify-center">
                        <Layers className="w-4 h-4 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <div className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider">
                          Composizione del kit
                        </div>
                        <div className="text-sm font-semibold">
                          Questo articolo include {product.kitItems!.length} prodotti
                        </div>
                      </div>
                    </div>
                    <ul className="divide-y divide-amber-100">
                      {product.kitItems!.map((it, idx) => (
                        <li
                          key={`${it.sku}-${idx}`}
                          className="flex items-center gap-3 px-4 py-2.5"
                          data-testid={`product-detail-kit-item-${it.sku}`}
                        >
                          <div className="w-7 h-7 rounded-md bg-white border border-amber-100 flex items-center justify-center flex-shrink-0">
                            <Package className="w-3.5 h-3.5 text-amber-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{it.label}</div>
                            <div className="text-[10px] font-mono text-muted-foreground">{it.sku}</div>
                          </div>
                          <div className="text-xs font-semibold tabular-nums text-amber-700 bg-amber-50 px-2 py-0.5 rounded">
                            ×{it.qty}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Descrizione tecnica */}
                {descrizione && (
                  <div className="mb-6">
                    <h2 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[hsl(214,58%,41%)]" />
                      Descrizione
                    </h2>
                    <div className="rounded-lg border bg-[hsl(var(--dtk-canvas))] p-4 text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap break-words">
                      {descrizione}
                    </div>
                  </div>
                )}

                <ProductAttachments attachments={product.attachments || []} />

                {/* Compatibilità stampanti */}
                {compatList.length > 0 && (
                  <div className="mb-6">
                    {/* Il titolo non condivide più la riga col campo di
                        filtro: su schermi stretti si comprimevano a vicenda. */}
                    <h2 className="text-sm font-semibold flex items-center gap-2 mb-2">
                      <Printer className="w-4 h-4 text-[hsl(214,58%,41%)]" />
                      Compatibilità
                      <span className="text-[10px] font-normal text-muted-foreground ml-1">({compatList.length})</span>
                    </h2>

                    <div className="relative mb-3 sm:max-w-xs">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                      <input
                        type="text"
                        value={compatQuery}
                        onChange={e => setCompatQuery(e.target.value)}
                        placeholder="Filtra modelli"
                        aria-label="Filtra i modelli compatibili"
                        className="tap w-full h-11 sm:h-9 pl-10 pr-3 rounded-md border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]"
                        data-testid="compat-search-input"
                      />
                    </div>

                    {filteredCompatList.length > 0 ? (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="product-compat-list">
                          {visibleCompatList.map(m => (
                            <Link
                              key={m.id}
                              to={`/consumabili-per/${m.id}`}
                              className="tap flex items-center gap-2 px-3 py-2.5 rounded-lg border bg-[hsl(var(--dtk-canvas))] text-sm hover:border-[hsl(214,58%,41%)] hover:bg-white transition-colors"
                              data-testid="product-compat-item"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                              <span className="font-mono text-[13px] text-muted-foreground">{m.marca}</span>
                              <span className="font-mono text-[13px]">{m.nome}</span>
                            </Link>
                          ))}
                        </div>

                        {/* Renderizzare tutte le righe portava il documento a
                            ~14.700px, l'83% del quale era questa lista. */}
                        {compatHiddenCount > 0 && (
                          <button
                            type="button"
                            onClick={() => setShowAllCompat(true)}
                            data-testid="compat-show-all"
                            className="tap mt-3 w-full h-11 rounded-lg border bg-white text-sm font-medium text-[hsl(214,58%,41%)] hover:bg-[hsl(var(--dtk-canvas))] transition-colors"
                          >
                            Mostra tutti i {filteredCompatList.length} modelli
                          </button>
                        )}
                        {showAllCompat && !compatFiltering && filteredCompatList.length > COMPAT_PREVIEW && (
                          <button
                            type="button"
                            onClick={() => setShowAllCompat(false)}
                            data-testid="compat-show-less"
                            className="tap mt-3 w-full h-11 rounded-lg border bg-white text-sm font-medium text-muted-foreground hover:bg-[hsl(var(--dtk-canvas))] transition-colors"
                          >
                            Mostra meno
                          </button>
                        )}
                      </>
                    ) : (
                      <p className="text-[13px] text-muted-foreground" data-testid="product-compat-empty">Nessun modello trovato.</p>
                    )}
                  </div>
                )}

                {/* Segnalazione difformità */}
                <div className="mt-6 pt-6 border-t">
                  {!user ? (
                    <Link
                      to="/accedi"
                      className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors"
                      data-testid="report-issue-login-link"
                    >
                      <AlertTriangle className="w-3.5 h-3.5" /> Accedi per segnalare una difformità su questo prodotto
                    </Link>
                  ) : !showReportForm ? (
                    <button
                      onClick={() => setShowReportForm(true)}
                      className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors"
                      data-testid="report-issue-button"
                    >
                      <AlertTriangle className="w-3.5 h-3.5" /> Segnala difformità su questo prodotto
                    </button>
                  ) : (
                    <div className="rounded-lg border p-4 bg-[hsl(var(--dtk-canvas))]">
                      <h3 className="text-sm font-semibold mb-2">Segnala Difformità</h3>
                      <textarea
                        value={reportText}
                        onChange={e => setReportText(e.target.value)}
                        placeholder="Descrivi il problema riscontrato..."
                        className="w-full h-20 px-3 py-2 rounded-lg border text-sm resize-none bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]"
                        disabled={submittingReport}
                        data-testid="report-textarea"
                      />
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={async () => {
                            if (!reportText.trim() || !product) return;
                            setSubmittingReport(true);
                            try {
                              await difformityService.create({
                                sku: product.sku,
                                productTitle: product.title,
                                productBrand: product.brand || '',
                                message: reportText.trim(),
                              });
                              toast.success('Segnalazione inviata al team DTK');
                              setShowReportForm(false);
                              setReportText('');
                            } catch {
                              toast.error('Errore durante l\'invio della segnalazione. Riprova.');
                            } finally {
                              setSubmittingReport(false);
                            }
                          }}
                          disabled={submittingReport || !reportText.trim()}
                          className="h-8 px-3 rounded-lg bg-[hsl(214,58%,41%)] text-white text-xs font-medium disabled:opacity-60"
                          data-testid="report-submit"
                        >
                          {submittingReport ? 'Invio…' : 'Invia'}
                        </button>
                        <button
                          onClick={() => setShowReportForm(false)}
                          disabled={submittingReport}
                          className="h-8 px-3 rounded-lg border text-xs font-medium"
                          data-testid="report-cancel"
                        >
                          Annulla
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Visti di Recente (al posto dei "Prodotti Correlati") */}
            {/* Ultimo blocco su mobile: il cross-sell non interrompe più la
                lettura delle specifiche, come faceva a metà pagina. */}
            <div className="order-[80] lg:order-none">
              <RecentlyViewed excludeSku={sku} />
            </div>
          </div>

          {/* ── Sidebar ──────────────────────────────────────────────────── */}
          {/* `contents` su mobile: il wrapper non genera un box, quindi le
              quattro card diventano figlie dirette della griglia e si
              ordinano una a una fra i blocchi della colonna principale
              (sequenza in cima alla griglia). Da `lg` in su il wrapper
              torna un blocco sticky come prima. */}
          <div className="contents lg:block lg:space-y-4 lg:sticky lg:top-28 lg:self-start">
            {/* ``lg:top-28`` (112px) lascia spazio all'header sticky in desktop:
                Layer 1 logo+search (h-16=64px) + Layer 2 nav blu (h-11=44px). */}
            {/* Prezzo + Disponibilità */}
            <div ref={buyBoxRef} className="order-[30] lg:order-none rounded-xl border bg-white p-5">
              {/* Prezzo */}
              {!isAuthenticated ? (
                <div className="mb-4">
                  <LoginToBuy variant="compact" />
                </div>
              ) : formattedPrice ? (
                <div className="mb-3">
                  {promoActive && (
                    <div className="mb-2 -mx-1 flex flex-wrap items-center gap-1.5" data-testid="product-detail-promo-banner">
                      {/* Badge principale: ⚡ per offerta a tempo, rosso fisso per permanente */}
                      {!isPermanentPromo ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-md bg-gradient-to-br from-[#1E4D8C] to-[#2B5EA7] text-white shadow-sm">
                          <Zap className="w-2.5 h-2.5" fill="currentColor" />
                          {promo!.label || 'Offerta'}
                        </span>
                      ) : null}
                      {/* Badge % sconto — sempre rosso */}
                      {sconto > 0 && (
                        <span className="text-[10px] font-bold px-1.5 py-1 rounded-md bg-red-500 text-white" data-testid="product-detail-promo-discount">
                          -{sconto}%
                        </span>
                      )}
                      {/* Countdown — solo per offerte a tempo */}
                      {!isPermanentPromo && promoCountdown && !promoCountdown.expired && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold tabular-nums px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-800" data-testid="product-detail-promo-countdown">
                          <Timer className="w-2.5 h-2.5 animate-pulse" />
                          Restano {promoCountdown.text}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className={`text-2xl font-bold tabular-nums ${promoActive ? 'text-red-600' : 'text-[hsl(214,58%,41%)]'}`} data-testid="product-price">
                      € {formattedPrice}
                    </span>
                    {promoActive && formattedListino && (
                      <span className="text-base text-gray-600 line-through tabular-nums" data-testid="product-price-old">
                        € {formattedListino}
                      </span>
                    )}
                    {product?.listino10IsMock && !promoActive && (
                      <span
                        className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
                        data-testid="product-price-indicative-badge"
                        title="Prezzo stimato — listino ERP non disponibile per questo articolo"
                      >
                        Indicativo
                      </span>
                    )}
                    <RibassoBadge ribasso={product?.ribasso} />
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    {promoActive
                      ? isPermanentPromo
                        ? 'Prezzo scontato — IVA esclusa, riservato B2B'
                        : 'Prezzo promo a tempo — IVA esclusa, riservato B2B'
                      : product?.listino10IsMock
                      ? 'Prezzo indicativo — verrà confermato dal nostro ufficio commerciale'
                      : 'Prezzo netto, IVA esclusa — Riservato B2B'}
                  </div>
                  {/* Sconto scatola — etichetta inline con tooltip blu */}
                  {product?.sku && product?.listino10 && PRODUCT_BOX_DISCOUNTS[product.sku] ? (() => {
                    const box = PRODUCT_BOX_DISCOUNTS[product.sku];
                    const cadBox = box.boxPrice / box.boxQty;
                    const sconto = Math.round((1 - cadBox / product.listino10!) * 100);
                    if (sconto <= 0) return null;
                    return (
                      <div className="relative mt-2 inline-flex items-center gap-2">
                        <div className="group/box inline-flex items-center gap-1" data-testid={`box-discount-inline-${product.sku}`}>
                          <span className="text-[11px] text-emerald-700 font-medium">
                            Sconto scatola: -{sconto}%
                          </span>
                          <span className="w-3.5 h-3.5 rounded-full bg-[hsl(214,58%,41%)] text-white text-[8px] font-bold flex items-center justify-center cursor-help">i</span>
                          <div className="absolute top-full left-0 mt-1.5 hidden group-hover/box:block z-50">
                            <div className="bg-[hsl(214,58%,41%)] text-white rounded-lg p-3 shadow-lg text-[11px] leading-relaxed whitespace-nowrap">
                              <div>Prezzo listino: € {product.listino10!.toFixed(2).replace('.', ',')}</div>
                              <div>Prezzo scontato: € {cadBox.toFixed(2).replace('.', ',')}</div>
                              <div>Sconto scatola intera: -{sconto}%</div>
                              <div className="mt-1 pt-1 border-t border-white/30">
                                Q.tà scatola: {box.boxQty} pz — A scatola intera: € {box.boxPrice.toFixed(2).replace('.', ',')}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })() : null}
                </div>
              ) : (
                <div className="mb-3">
                  <div className="text-sm text-muted-foreground italic" data-testid="product-price-na">
                    Prezzo su richiesta
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Contatta il nostro ufficio commerciale
                  </div>
                </div>
              )}

              {/* Stock */}
              <div className="flex flex-col gap-2 mb-4" data-testid="product-stock-status">
                <div className="flex items-center gap-2 flex-wrap">
                  {isNotSellable ? (
                    <>
                      <div className="w-2.5 h-2.5 rounded-full bg-gray-400" />
                      <span className="text-sm font-medium text-gray-600">Non disponibile</span>
                    </>
                  ) : !hasStock ? (
                    <>
                      <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                      <span className="text-sm font-medium text-amber-600">Disponibilità da verificare</span>
                    </>
                  ) : isOutOfStock && product?.prossimArrivo ? (
                    <>
                      <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                      <StockArrivalBadge
                        prossimArrivo={product.prossimArrivo}
                        qtaArrivo={product.qtaArrivo}
                        variant="text"
                      />
                    </>
                  ) : isOutOfStock ? (
                    <>
                      <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                      <span className="text-sm font-medium text-red-600">Non disponibile</span>
                    </>
                  ) : (
                    <>
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="text-sm font-medium text-emerald-700">
                        Disponibile{effectiveStock > 0 ? ` (${effectiveStock} pz)` : ''}
                      </span>
                    </>
                  )}
                </div>
                {/* Prodotto disponibile con arrivo programmato: testo grigio
                    cliccabile "Prossimi arrivi" (non un chip/pill, per non
                    confonderlo con lo stato di disponibilità — richiesta
                    utente 10/07/2026), stesso comportamento (apre la
                    stessa modale) del chip precedente. */}
                {!isNotSellable && hasStock && !isOutOfStock && product?.prossimArrivo && (
                  <StockArrivalBadge
                    prossimArrivo={product.prossimArrivo}
                    qtaArrivo={product.qtaArrivo}
                    variant="muted"
                  />
                )}
              </div>

              {/* Add to cart / Alert */}
              {!isAuthenticated ? (
                <LoginToBuy variant="cta" />
              ) : isNotSellable ? (
                <button
                  type="button"
                  disabled
                  className="w-full h-11 rounded-lg text-sm font-medium flex items-center justify-center gap-2 bg-gray-100 text-gray-500 border border-gray-200 cursor-not-allowed"
                  data-testid="product-not-sellable-button"
                  title="Articolo non acquistabile online — contatta il commerciale"
                >
                  <AlertTriangle className="w-4 h-4" />
                  Non disponibile
                </button>
              ) : isOutOfStock ? (
                alreadyInCart > 0 && stockQty > 0 ? (
                  /* Tutto lo stock grezzo è già nel carrello */
                  <div
                    className="w-full h-10 rounded-lg text-sm font-medium flex items-center justify-center gap-2 bg-amber-50 text-amber-700 border border-amber-200"
                    data-testid="stock-all-in-cart"
                  >
                    Hai già {alreadyInCart} pz nel carrello (disponibilità esaurita)
                  </div>
                ) : (
                  <button
                    onClick={async () => {
                      if (!user) { toast.error('Accedi per attivare l\'avviso'); return; }
                      const alertActive = isActive(product!.sku);
                      try {
                        if (alertActive) {
                          await removeAlert(product!.sku);
                          toast.success('Avviso rimosso');
                        } else {
                          await addAlert({
                            sku: product!.sku,
                            title: product!.title,
                            brand: product!.brand,
                            category: product!.category,
                          });
                          toast.success('Riceverai una notifica quando disponibile');
                        }
                      } catch {
                        toast.error('Errore, riprova');
                      }
                    }}
                    className={`w-full h-10 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                      isActive(product?.sku ?? '')
                        ? 'bg-blue-100 text-blue-700 border border-blue-200 hover:bg-blue-200'
                        : 'bg-[#8a1719] text-white hover:bg-[#701315]'
                    }`}
                    data-testid="notify-stock-button"
                  >
                    <Bell className="w-4 h-4" />
                    {isActive(product?.sku ?? '') ? 'Alert attivato — clicca per rimuovere' : 'Avvisami quando disponibile'}
                  </button>
                )
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground">Qtà:</span>
                    <QtyInput
                      value={addQty}
                      onChange={setAddQty}
                      max={maxQty}
                      onExceedMax={q => setAddQty(q)}
                    />
                  </div>

                  {/* Nudge sconto scatola — appare quando si è ad 1 pezzo dalla scatola */}
                  {product?.sku && product?.listino10 ? (
                    <BoxNudge
                      sku={product.sku}
                      qty={addQty}
                      unitPrice={product.listino10}
                      onSetQty={(q) => setAddQty(q)}
                      onBoxCompleted={(boxes, bq) => toast.success(`Scatola da ${bq} pz completata — prezzo scatola applicato!`)}
                    />
                  ) : null}

                  {/* Disponibilità parziale */}
                  {hasStock && addQty > effectiveStock && effectiveStock > 0 && (
                    <StockWarning
                      sku={product.sku}
                      qtyRequested={addQty}
                      stockAvailable={effectiveStock}
                      variant="inline"
                      onOrderAvailable={() => {
                        addItem(product, effectiveStock);
                        addAlert({ sku: product.sku, title: product.title, brand: product.brand, category: product.category, qtyWanted: addQty - effectiveStock }).catch(() => {});
                        toast.success(`${effectiveStock} pz aggiunti al carrello + alert per i restanti ${addQty - effectiveStock}`);
                        setAddQty(1);
                      }}
                      onAddAvailableOnly={() => {
                        addItem(product, effectiveStock);
                        toast.success(`${effectiveStock} pz aggiunti al carrello`);
                        setAddQty(1);
                      }}
                    />
                  )}

                  <button
                    onClick={handleAddToCart}
                    disabled={qtyExceedsStock}
                    data-testid="product-add-to-cart"
                    title={qtyExceedsStock ? 'Quantità superiore alla giacenza disponibile — scegli un\'opzione sopra' : undefined}
                    className={`w-full h-11 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                      qtyExceedsStock
                        ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                        : isInCart
                        ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                        : 'bg-[hsl(214,58%,41%)] text-white hover:bg-[hsl(214,58%,46%)]'
                    }`}
                  >
                    {isInCart ? (
                      <><Check className="w-4 h-4" /> Nel carrello — Aggiungi altri</>
                    ) : (
                      <><ShoppingCart className="w-4 h-4" /> Aggiungi al carrello</>
                    )}
                  </button>
                </div>
              )}

              <p className="text-[10px] text-muted-foreground mt-3 text-center">
                Spedizione in 24H
              </p>
            </div>

            {/* Stock info card */}
            {hasStock && (
              <div className="order-[50] lg:order-none rounded-xl border bg-white p-4">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Warehouse className="w-3.5 h-3.5" /> Giacenza Magazzino
                </h3>
                {/* Stesso numero del buy box (`effectiveStock`). Prima qui
                    c'era la giacenza grezza, diversa dai "N pz" della riga
                    Disponibile: due cifre per la stessa disponibilità si
                    leggono come un bug, a qualunque distanza stiano le due
                    card. La riga ambra sotto conserva il dato ERP grezzo. */}
                <div className="text-2xl font-bold text-[hsl(214,58%,41%)]" data-testid="product-stock-qty">
                  {effectiveStock} <span className="text-sm font-normal text-muted-foreground">pz disponibili</span>
                </div>
                {alreadyInCart > 0 && (
                  <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1.5 font-medium">
                    {stockQty} pz in giacenza — {alreadyInCart} già nel carrello
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground mt-1">Aggiornato ogni ora da ERP</div>
              </div>
            )}

            {/* Info card */}
            <div className="order-[60] lg:order-none rounded-xl border bg-white p-5">
              <h3 className="text-sm font-semibold mb-3">Informazioni</h3>
              <div className="space-y-3 text-sm">
                {[
                  'Qualità testata e certificata',
                  'Garanzia 12 mesi',
                  'Spedizione in 24 ore',
                ].map(info => (
                  <div key={info} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{info}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Assistenza */}
            <div className="order-[70] lg:order-none rounded-xl border bg-[hsl(210,40%,93%)] p-5">
              <h3 className="text-sm font-semibold mb-2">Hai bisogno di aiuto?</h3>
              <p className="text-xs text-muted-foreground mb-3">Il nostro team tecnico è a tua disposizione.</p>
              <div className="space-y-1.5 text-sm">
                <div className="font-mono text-xs">+39 06 5135753</div>
                <div className="font-mono text-xs text-[hsl(var(--dtk-focus))]">info@dtksrl.eu</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Barra acquisto sticky (solo mobile) ────────────────────────────
          Mostra prezzo e CTA quando il buy box è fuori dal viewport. Le
          condizioni di visibilità replicano quelle del ramo CTA primario
          del buy box: niente barra per guest, articoli non vendibili o
          fuori stock, dove il buy box non offre un acquisto ma un login,
          un avviso di disponibilità o un messaggio. */}
      {isAuthenticated && !isNotSellable && !isOutOfStock && formattedPrice && (
        <div
          data-testid="product-buy-bar"
          aria-hidden={!buyBoxOffscreen}
          className={`lg:hidden fixed inset-x-0 bottom-0 z-[70] flex items-center gap-3 border-t bg-white px-4 py-3 shadow-[0_-4px_16px_rgb(0_0_0/0.08)] transition-transform duration-200 motion-reduce:transition-none ${
            buyBoxOffscreen ? 'translate-y-0' : 'translate-y-full'
          }`}
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
        >
          <div className="min-w-0">
            <div className="text-[11px] text-muted-foreground leading-none">IVA esclusa</div>
            <div
              className={`text-lg font-bold tabular-nums leading-tight ${promoActive ? 'text-red-600' : 'text-[hsl(214,58%,41%)]'}`}
            >
              € {formattedPrice}
            </div>
          </div>
          <button
            onClick={handleAddToCart}
            disabled={qtyExceedsStock}
            tabIndex={buyBoxOffscreen ? 0 : -1}
            data-testid="product-buy-bar-cta"
            className={`tap flex-1 h-12 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              qtyExceedsStock
                ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                : isInCart
                ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                : 'bg-[hsl(214,58%,41%)] text-white hover:bg-[hsl(214,58%,46%)]'
            }`}
          >
            {isInCart ? (
              <><Check className="w-4 h-4" /> Aggiungi altri</>
            ) : (
              <><ShoppingCart className="w-4 h-4" /> Aggiungi al carrello</>
            )}
          </button>
        </div>
      )}

      {/* Kit Detail Modal */}
      {isKit && (
        <KitDetailModal
          open={showKitModal}
          onClose={() => setShowKitModal(false)}
          productSku={product.sku}
          productTitle={product.title}
          items={product.kitItems!}
        />
      )}
    </div>
  );
}
